import React, { useState } from "react";
import { useActor } from "@xstate/react";
import TextInput from "../TextInput";
import { Button } from "../SignUpButton";
import { Loader } from "@pushpress/shared-components";
import { useSignupQuery } from "../../../../apis/authApi.hooks";
import { useGenerateCodeQuery } from "../../../../apis/loginApi.hooks";
import { toast } from "react-toastify";
import { useAnalytics } from "../../../../components/providers/AnalyticsProvider";
import { SignUpEvent } from "../../constants/signup.events";
import { useSignUpMachine } from "../../context/SignUpMachineContext";
import { SignUpFormField } from "../../constants/signup-form-field.enum";
import { BalancedStepSubtitle } from "../BalancedStepSubtitle";
import { BalancedStepTitle } from "../BalancedStepTitle";
import { TextInputType } from "../../constants/signup.types";
import { getSignUpPayload } from "../../utils/signup-payload";

const FreeStep: React.FC = () => {
  const machine = useSignUpMachine();
  const [state, send] = useActor(machine);
  const { formData, utm } = state.context;

  const [isLoading, setIsLoading] = useState(false);
  const [unlockedFields, setUnlockedFields] = useState<Set<string>>(new Set());
  const signup = useSignupQuery();
  const generateCode = useGenerateCodeQuery();
  const { trackEvent } = useAnalytics();

  const fields = [
    {
      id: SignUpFormField.FirstName,
      inputType: "text",
      label: "First Name",
      required: true,
      locked: true,
      placeholder: "Coach",
    },
    {
      id: SignUpFormField.LastName,
      inputType: "text",
      label: "Last Name",
      required: true,
      locked: true,
      placeholder: "Smith",
    },
    {
      id: SignUpFormField.Phone,
      inputType: "phone",
      label: "Phone Number",
      required: true,
      locked: true,
      placeholder: "(555) 123-4567",
    },
    {
      id: SignUpFormField.Email,
      inputType: "email",
      label: "Email",
      required: true,
      locked: true,
      placeholder: "coach@yourgym.com",
    },
  ];

  const handleUnlock = (fieldId: string) => {
    setUnlockedFields((prev) => new Set(prev).add(fieldId));
  };

  const handleChange = (fieldId: string, value: string) => {
    send({ type: "UPDATE_FORM", data: { [fieldId]: value } });
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    const signupPayload = getSignUpPayload({ formData, utm });

    try {
      const signupResult = await signup.mutateAsync(signupPayload);

      if (signupResult.uuid) {
        send({ type: "SET_CLIENT_UUID", uuid: signupResult.uuid });
      } else {
        console.error("No uuid returned from signup");
      }

      if (signupResult.subdomain) {
        send({ type: "SET_SUBDOMAIN", subdomain: signupResult.subdomain });
      } else {
        console.error("No subdomain returned from signup");
      }

      if (signupResult.email) {
        await generateCode.mutateAsync({ email: signupResult.email });
        send({ type: "NEXT" });
      } else {
        console.error("No email returned from signup");
      }
    } catch (error) {
      console.error("Signup failed:", error);
      trackEvent(SignUpEvent.SignUpFailed, {
        step: "free",
        error: error.message,
      });
      toast.error("Error sending code. Please try again.");
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="tw-flex tw-flex-col tw-items-center tw-space-y-1 tw-text-center">
        <BalancedStepTitle title="Create a free account" />
        <BalancedStepSubtitle subtitle="Get started with our free tier" />
      </div>
      <div className="tw-space-y-4">
        {fields.map((field) => {
          const isLocked = field.locked && !unlockedFields.has(field.id);
          return (
            <TextInput
              key={field.id}
              id={field.id}
              value={formData[field.id] as string}
              onChange={(value) => handleChange(field.id, value)}
              inputType={field.inputType as TextInputType}
              label={field.label}
              required={field.required}
              disabled={isLocked}
              onEnable={() => handleUnlock(field.id)}
              placeholder={field.placeholder}
            />
          );
        })}
      </div>
      <div className="tw-flex tw-justify-between tw-items-center">
        <Button
          onClick={handleSubmit}
          disabled={isLoading}
          size="fat"
          className="tw-uppercase tw-tracking-widest tw-w-full tw-flex tw-py-4 tw-items-center tw-gap-2 tw-text-white"
        >
          {isLoading ? <Loader color="white" /> : <span>Send Code</span>}
        </Button>
      </div>
    </>
  );
};

export default FreeStep;
